import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { SafeText } from "@/shared/components/SafeText";
import { TestPartitionBadge } from "@/shared/components/TestPartitionBadge";
import { Badge } from "@/shared/components/ui/badge";
import { formatDateShort } from "@/shared/lib/format-date";
import { organizationPath } from "@/shared/organizations/organization-link";
import { InvitationLocations } from "./InvitationLocations";
import { InvitationStatusBadge } from "./InvitationStatusBadge";
import type { AdminInvitation } from "./types";

interface InvitationsTableProps {
  readonly items: readonly AdminInvitation[];
  /** Drop the organization column when the list is already scoped to one organization. */
  readonly hideOrganization?: boolean;
}

const TH_CLASS =
  "px-3 py-2 text-left font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground";
const TD_CLASS = "px-3 py-3 align-top font-sans text-sm text-foreground";
const COLUMNS = [
  "email",
  "role",
  "status",
  "inviter",
  "organization",
  "locations",
  "created",
  "expires",
  "accepted",
] as const;

export function InvitationsTable({ items, hideOrganization = false }: InvitationsTableProps) {
  const { t, i18n } = useTranslation();
  const columns = hideOrganization ? COLUMNS.filter((c) => c !== "organization") : COLUMNS;
  const date = (iso: string | null) => formatDateShort(iso, i18n.language) || "—";

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table data-testid="invitations-table" className="w-full border-collapse">
        <caption className="sr-only">{t("invitations.table.caption")}</caption>
        <thead className="border-b border-border bg-muted/30">
          <tr>
            {columns.map((column) => (
              <th key={column} scope="col" className={TH_CLASS}>
                {t(`invitations.table.${column}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr
              key={i.id}
              data-testid={`invitation-row-${i.id}`}
              className="border-b border-border last:border-0 hover:bg-muted/20"
            >
              <th scope="row" className={`${TD_CLASS} break-all text-left font-medium`}>
                <SafeText>{i.email}</SafeText>
              </th>
              <td className={TD_CLASS}>
                <Badge variant="outline">{t(`invitations.role.${i.membershipRole}`)}</Badge>
              </td>
              <td className={TD_CLASS}>
                <InvitationStatusBadge status={i.status} testId={`invitation-status-${i.id}`} />
              </td>
              <td className={TD_CLASS}>
                <SafeText fallback={t("organizations.unnamedUser")}>
                  {i.inviter.displayName}
                </SafeText>
              </td>
              {!hideOrganization && (
                <td className={TD_CLASS}>
                  {i.organization ? (
                    <span className="flex flex-wrap items-center gap-2">
                      <Link
                        to={organizationPath(i.organization.id)}
                        data-testid={`invitation-organization-${i.id}`}
                        className="break-words font-medium text-primary underline-offset-2 hover:underline"
                      >
                        <SafeText fallback={t("organizations.unnamed")}>
                          {i.organization.name}
                        </SafeText>
                      </Link>
                      <TestPartitionBadge
                        isTest={i.organization.isTest}
                        testId={`invitation-test-${i.id}`}
                      />
                    </span>
                  ) : (
                    <span className="text-muted-foreground">{t("invitations.noOrganization")}</span>
                  )}
                </td>
              )}
              <td className={TD_CLASS}>
                <InvitationLocations invitationId={i.id} locations={i.locations} />
              </td>
              <td className={`${TD_CLASS} whitespace-nowrap text-muted-foreground`}>
                {date(i.createdAt)}
              </td>
              <td className={`${TD_CLASS} whitespace-nowrap text-muted-foreground`}>
                {date(i.expiresAt)}
              </td>
              <td className={`${TD_CLASS} whitespace-nowrap text-muted-foreground`}>
                {date(i.acceptedAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
